import * as React from "react";

import connectToList from "../../store/list";

import GenButton from "../Gen/Button";
import GenCheckboxesFilter from "../Gen/CheckboxesFilter";
import GenCheckboxFilter from "../Gen/CheckboxFilter";
import GenFilterGroup from "../Gen/FilterGroup";
import GenMore from "../Gen/More";
import GenPanel from "../Gen/Panel";
import GenPanelHeader from "../Gen/PanelHeader";
import GenPanelSlice from "../Gen/PanelSlice";
import GenSidebarContainer from "../Gen/SidebarContainer";
import GenSidebarListControls from "../Gen/SidebarListControls";
import GenSortButtons from "../Gen/SortButtons";

import DocumentSummary from "../Document/Summary";

import {canPossiblyAccess} from "../../src/role_definition_helpers";
import GenYMWDFilter from "../Gen/YMWDFilter";

import useConnectedListHistory from "../../hooks/useConnectedListHistory";

interface MdataStringDefinition {
  type: "MDATA_STRING";
  count?: "MDATA_MANY";
  values: Array<string>;
  display_title?: string;

}

interface MdataYmwdDefinition {
  type: "MDATA_YMWD"
}

type DocumentMdataKeys = "hideSidebar" | "gender" | "race" | "age_group" | "organ_system" | "clinical_location" | "secondary_location" | "apn_area_of_study" | "coaemsp_pathology" | "pance_medical_content_categories" | "specialty" | "reeldx_libraries"

interface DocumentListProps {
  documents: ConnectedList<DocumentSnakeModel>;
  isWithGroupHeader: boolean;
  current_user: UserSnakeModel;
  group: GroupSnakeModel;
  current_role_definition: RoleDefinitionSnakeModel;
  document_mdata_definition: Record<DocumentMdataKeys, MdataStringDefinition>;
  hideSidebar: boolean;
  canCreateCategory: boolean;
}

function createCategoryUrl(search?: string, activeFilters?: Record<string, Filter>) {
  const data: {search?: string, where?: string} = {};

  // Search
  if (search && (search !== "")) {
    data.search = search;
  }

  if (activeFilters && Object.keys(activeFilters).length > 0) {
    data.where = JSON.stringify(Object.values(activeFilters));
  }

  return `/categories/new?${(new URLSearchParams({search_filters: JSON.stringify(data)})).toString()}`;
}

function DocumentList(
  {
    documents,
    isWithGroupHeader,
    current_user,
    group,
    current_role_definition,
    document_mdata_definition,
    hideSidebar,
    canCreateCategory,
  }: DocumentListProps,
) {
  useConnectedListHistory("documents", documents);

  React.useEffect(() => {
    documents.mountList("/documents");
  }, []);

  return (
    <GenSidebarContainer>
      <GenSidebarListControls
        connectedList={documents}
        title="Cases"
        search
        searchPlaceholder="Search by symptom, diagnosis, or case #..."
        hideSidebar={hideSidebar}
      >
        <div className="gen-list-controls-actions">
          {canPossiblyAccess(current_role_definition, "document", "create") && (
            <GenButton key="Create" name="Create Case" highlighted href="/documents/new"/>
          )}
          {current_user?.is_admin && (
            <>
              <GenButton key="Lend" name="Lend Cases" highlighted href="/group_documents/new_batch"/>
              <GenButton key="Revoke" name="Revoke Cases" highlighted href="/group_documents/delete_batch"/>
              <GenButton key="Report" name="Cases Report" highlighted href="/documents.csv"/>
            </>
          )}
        </div>
        <div>
          <GenSortButtons
            {...documents}
            sortOptions={[
              {
                "title": "Case #",
                "value": "documents.id",
              },
              {
                "title": "Published",
                "value": "documents.created_at",
              },
            ]}
          />
        </div>
        <GenCheckboxFilter
          {...documents}
          filterKey="extra_media"
          option={["ilike", "documents.description", "%extra_media%"]}
          label="Only cases with other media"
        />
        <GenCheckboxFilter
          {...documents}
          filterKey="patient_interaction_physical_exam"
          option={["@>", "documents.mdata", {patient_interaction: ["Physical Exam"]}]}
          label="Only cases with a physical exam"
        />
        <GenCheckboxFilter
          {...documents}
          filterKey="patient_interaction_procedure"
          option={["@>", "documents.mdata", {patient_interaction: ["Procedure"]}]}
          label="Only cases with a procedure"
        />
        <GenFilterGroup title="Demographics">
          <GenCheckboxesFilter
            {...documents}
            filterKey="gender"
            filterColumn="documents.mdata"
            title="Gender"
            dataType="MDATA"
            options={document_mdata_definition.gender.values}
          />
          <GenCheckboxesFilter
            {...documents}
            filterKey="race"
            filterColumn="documents.mdata"
            title="Race"
            dataType="MDATA"
            dataCount="MDATA_MANY"
            options={document_mdata_definition.race.values}
          />
          <GenCheckboxesFilter
            {...documents}
            filterKey="age_group"
            filterColumn="documents.mdata"
            title="Age Category"
            dataType="MDATA"
            dataCount="MDATA_MANY"
            options={document_mdata_definition.age_group.values}
          />
          <GenYMWDFilter
            {...documents}
            filterKey="age"
            filterColumn="documents.mdata"
            title="Chronological Age"
          />
        </GenFilterGroup>
        <GenCheckboxesFilter
          {...documents}
          filterKey="organ_system"
          filterColumn="documents.mdata"
          title="Organ System"
          displayLikeFilterGroup
          dataType="MDATA"
          dataCount="MDATA_MANY"
          options={document_mdata_definition.organ_system.values}
        />
        <GenFilterGroup title="Location">
          <GenCheckboxesFilter
            {...documents}
            filterKey="clinical_location"
            filterColumn="documents.mdata"
            title="Clinical Location"
            dataType="MDATA"
            dataCount="MDATA_MANY"
            options={document_mdata_definition.clinical_location.values}
          />
          <GenCheckboxesFilter
            {...documents}
            filterKey="secondary_location"
            filterColumn="documents.mdata"
            title="Secondary Location"
            dataType="MDATA"
            dataCount="MDATA_MANY"
            options={document_mdata_definition.secondary_location.values}
          />
        </GenFilterGroup>
        <GenFilterGroup title="APN Curriculum">
          <GenCheckboxesFilter
            {...documents}
            filterKey="apn_area_of_study"
            filterColumn="documents.mdata"
            title="Area of Study"
            dataType="MDATA"
            dataCount="MDATA_MANY"
            options={document_mdata_definition.apn_area_of_study.values}
          />
        </GenFilterGroup>
        <GenFilterGroup title="CoAEMSP Curriculum">
          <GenCheckboxesFilter
            {...documents}
            filterKey="coaemsp_pathology"
            filterColumn="documents.mdata"
            title="Pathology or Patient Complaint"
            dataType="MDATA"
            dataCount="MDATA_MANY"
            options={document_mdata_definition.coaemsp_pathology.values}
          />
        </GenFilterGroup>
        <GenFilterGroup title="PANCE">
          <GenCheckboxesFilter
            {...documents}
            filterKey="pance_medical_content_categories"
            filterColumn="documents.mdata"
            title="Medical Content Categories"
            dataType="MDATA"
            dataCount="MDATA_MANY"
            options={document_mdata_definition.pance_medical_content_categories.values}
          />
        </GenFilterGroup>
        <GenCheckboxesFilter
          {...documents}
          filterKey="specialty"
          filterColumn="documents.mdata"
          title="Medical Specialty"
          displayLikeFilterGroup
          dataType="MDATA"
          dataCount="MDATA_MANY"
          options={document_mdata_definition.specialty.values}
        />
        <GenCheckboxesFilter
          {...documents}
          filterKey="reeldx_libraries"
          filterColumn="documents.mdata"
          title="ReelDx Libraries"
          displayLikeFilterGroup
          dataType="MDATA"
          dataCount="MDATA_MANY"
          options={document_mdata_definition.reeldx_libraries.values}
        />
        {current_user?.is_admin && (
          <GenCheckboxesFilter
            {...documents}
            filterKey="publish_version_id"
            filterColumn="documents.mdata"
            title="Publishing Status"
            displayLikeFilterGroup
            dataType="BOOLEAN"
            options={[
              {
                displayName: "Published",
                value: true,
              },
              {
                displayName: "Not Published",
                value: false,
              },
            ]}
          />
        )}
        {current_user?.is_admin && (
          <GenCheckboxesFilter
            {...documents}
            filterKey="circulation"
            filterColumn="documents.circulation"
            title="Circulation"
            displayLikeFilterGroup
            dataType="REGULAR"
            options={[
              {
                displayName: "Public",
                value: "public_circulation",
              },
              {
                displayName: "Group",
                value: "group_circulation",
              },
              {
                displayName: "Invite",
                value: "invite_circulation",
              },
              {
                displayName: "None",
                value: "no_circulation",
              },
            ]}
          />
        )}
      </GenSidebarListControls>
      <div className="flex-grow">
        <div>
          { isWithGroupHeader && (
            <GenPanel>
              <GenPanelHeader
                rightContent={current_user?.is_admin ? [
                  <GenButton key="Edit" name="Edit Group" highlighted href="/edit"/>,
                  <GenButton key="Duplicate" name="Duplicate" highlighted href="/duplicate"/>,
                  <GenButton key="Members" name="Members" highlighted href="/group_role_definition_users"/>,
                ]: []}
              >
                {group.name}
              </GenPanelHeader>
              <GenPanelSlice>
                {group.description}
              </GenPanelSlice>
            </GenPanel>
          )}
        </div>

        <div className="gen-list-grid">
          {
            documents.listData?.map((document) => (
              <DocumentSummary
                document={document}
                key={document.id}
                canAddFromSummary
                currentUser={current_user}
                currentRoleDefinition={current_role_definition}
              />
            ))
          }
        </div>
        { !!documents.listData && documents.listData.length == 0 && <div key="no-items" className="gen-list-no-items"><h1>No Items</h1></div> }
        <div className="flex justify-center">
          <GenMore
            {...documents}
            shouldInfiniteScroll
          />
        </div>
      </div>
      {canCreateCategory && (
        <GenButton
          className="absolute-create-category"
          key="Create Filtered Category"
          name="Create Filtered Category"
          href={createCategoryUrl(documents.search, documents.activeFilters)}
          disabled={(!documents.activeFilters || Object.keys(documents.activeFilters).length === 0) && (!documents.search || documents.search === "")}
          highlighted
        />
      )}
    </GenSidebarContainer>
  );
}

export default connectToList(DocumentList, ["documents"]);
